import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PencilSimple } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const init = (p) => ({
  label: p.label || "", period_from: (p.period_from || "").slice(0, 10), period_to: (p.period_to || "").slice(0, 10),
  default_key: p.default_key || "living_area", external_weg_reference: p.external_weg_reference || "", notes: p.notes || "",
});

export default function PeriodMasterDataDialog({ period, catalog, onSaved }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(init(period));
  const [saving, setSaving] = useState(false);
  const isDraft = period.status === "draft";
  useEffect(() => { if (open) setF(init(period)); }, [open, period]);
  const set = (k) => (e) => setF((c) => ({ ...c, [k]: e?.target ? e.target.value : e }));

  const save = async () => {
    if (!f.period_from || !f.period_to || f.period_from >= f.period_to) {
      toast.error("Bitte gültigen Zeitraum angeben (Beginn vor Ende)."); return;
    }
    setSaving(true);
    try {
      await api.patch(`/bka/periods/${period.id}`, f);
      toast.success("Stammdaten gespeichert");
      setOpen(false); onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  return (
    <>
      <Button variant="outline" className="gap-1.5" onClick={() => setOpen(true)} disabled={!isDraft}
              title={isDraft ? "Stammdaten bearbeiten" : "Abgeschlossen — zum Bearbeiten erst „Wieder öffnen“"}
              data-testid="bka-master-edit-btn">
        <PencilSimple size={14} /> Stammdaten
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="bka-master-dialog">
          <DialogHeader>
            <DialogTitle>Stammdaten der Abrechnung</DialogTitle>
            <DialogDescription>Änderungen am Zeitraum oder Schlüssel wirken sich erst nach erneutem Berechnen aus.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Bezeichnung</Label><Input value={f.label} onChange={set("label")} data-testid="bka-master-label" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Zeitraum von</Label><Input type="date" value={f.period_from} onChange={set("period_from")} data-testid="bka-master-from" /></div>
              <div><Label>Zeitraum bis</Label><Input type="date" value={f.period_to} onChange={set("period_to")} data-testid="bka-master-to" /></div>
            </div>
            <div>
              <Label>Standard-Umlageschlüssel</Label>
              <Select value={f.default_key} onValueChange={set("default_key")}>
                <SelectTrigger data-testid="bka-master-key"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(catalog?.distribution_keys || []).map((k) => <SelectItem key={k.key} value={k.key}>{k.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Referenz WEG-Abrechnung</Label><Input value={f.external_weg_reference} onChange={set("external_weg_reference")} data-testid="bka-master-ref" /></div>
            <div><Label>Notizen (intern)</Label><Textarea rows={3} value={f.notes} onChange={set("notes")} data-testid="bka-master-notes" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
            <Button onClick={save} disabled={saving} data-testid="bka-master-save">{saving ? "Speichere…" : "Speichern"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
